"use client";

import { BillingIntervalToggle, type TBillingInterval } from "./BillingIntervalToggle";
import { PricingCard } from "./PricingCard";
import { TrialCopy } from "./TrialCopy";
import type { TPricingTier } from "@/lib/sanity/pricing";
import { cn } from "@/lib/utils";

export interface IPricingTableProps {
  tiers: TPricingTier[];
  interval: TBillingInterval;
  onIntervalChange: (interval: TBillingInterval) => void;
  /** Called when user selects a plan (CTA click). Parent handles checkout or navigation. */
  onSelectPlan?: (slug: string, interval: TBillingInterval) => void;
  /** Show "14-day free trial" or onboarding variant. */
  showTrialCopy?: boolean;
  /** Use onboarding copy ("Your 14-day trial has started…"). */
  trialCopyOnboarding?: boolean;
  /** When true, show primary CTA on cards (e.g. "Get started", "Continue to checkout"). */
  showCta?: boolean;
  /** Loading state for CTA (e.g. during checkout redirect). */
  isLoading?: boolean;
  /** Compact layout (e.g. for billing page overview). */
  compact?: boolean;
  /** Active subscription counts keyed by tier slug (for capped tiers). */
  subscriptionCounts?: Record<string, number> | undefined;
  className?: string;
}

/**
 * Reusable pricing table: interval toggle, trial copy (optional), and a card per tier.
 * Tiers are passed from server (Sanity). Parent controls interval state and onSelectPlan behavior.
 */
export function PricingTable({
  tiers,
  interval,
  onIntervalChange,
  onSelectPlan,
  showTrialCopy = false,
  trialCopyOnboarding = false,
  showCta = true,
  isLoading = false,
  compact = false,
  subscriptionCounts,
  className,
}: IPricingTableProps): React.ReactElement {
  return (
    <div className={cn("space-y-8", className)}>
      <div className="flex flex-col items-center gap-3 text-center">
        <BillingIntervalToggle value={interval} onChange={onIntervalChange} />
        {showTrialCopy && (
          <TrialCopy
            onboarding={trialCopyOnboarding}
            variant="inline"
            className="max-w-xl text-balance"
          />
        )}
      </div>
      <div
        className={
          compact
            ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            : "grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        }
      >
        {tiers.map((tier) => (
          <PricingCard
            key={tier._id}
            tier={tier}
            interval={interval}
            {...(onSelectPlan !== undefined && { onSelect: onSelectPlan })}
            showCta={showCta}
            isLoading={isLoading}
            soldCount={subscriptionCounts?.[tier.slug]}
          />
        ))}
      </div>
    </div>
  );
}
